import _ from 'lodash'
import mergeRefs from 'merge-refs'
import { forwardRef, InputHTMLAttributes, useRef } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { components } from 'react-select'
import AsyncSelect from 'react-select/async'

import cn from '@/utilities/cn'
import fetch from '@/utilities/fetch'

function parse(filter: string = '') {
  const result: { simple: boolean; users: string[]; groups: string[] } = { simple: true, users: [], groups: [] }

  for (const expression of filter.split(' or ').filter(Boolean)) {
    const [, attribute, , value] = /^(.+) eq ("?)(.+)(\2)$/.exec(expression) ?? []

    if (attribute === 'id' || attribute === 'urn:ietf:params:scim:schemas:core:2.0:User:id') {
      result.users.push(value)
      continue
    }

    if (attribute === 'groups.value' || attribute === 'urn:ietf:params:scim:schemas:core:2.0:User:groups.value') {
      result.groups.push(value)
      continue
    }

    if (attribute === 'active' || attribute === 'urn:ietf:params:scim:schemas:core:2.0:User:active') {
      result.groups.push('everyone')
      continue
    }

    result.simple = false
  }

  return result
}

function stringify({ users = [], groups = [] }: { users?: string[]; groups?: string[] }) {
  return [
    ...users.map(user => `id eq "${user}"`),
    ...groups.map(group => (group === 'everyone' ? 'active eq true' : `groups.value eq "${group}"`))
  ].join(' or ')
}

type AudienceProps = InputHTMLAttributes<HTMLInputElement> & { value?: string }

const Audience = forwardRef<HTMLInputElement, AudienceProps>((props, ref) => {
  const internalRef = useRef<HTMLInputElement>(null)

  const { simple, users, groups } = parse(props.value)

  function updateInputValue(input: HTMLInputElement | null, value: string) {
    if (!input) return

    // hidden inputs don't dispatch events
    input.style.display = 'none'
    input.type = 'text'

    // React intercepts setting `input.value` so we need to call the original set method
    Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value')?.set?.call(input, value)
    input.dispatchEvent(new Event('change', { bubbles: true }))

    // reset the input type
    input.type = 'hidden'
    input.style.display = 'initial'
  }

  return (
    <>
      <input
        ref={mergeRefs(ref, internalRef)}
        className="block w-full rounded-md border-gray-300 text-sm shadow-sm"
        type={simple ? 'hidden' : 'text'}
        {...props}
        value={props.value ?? ''}
      />

      {simple ? (
        <div className="grid grid-cols-2 gap-2">
          <div>
            <GroupsSelect
              value={groups}
              onChange={groups => {
                const value = stringify({ users, groups })
                updateInputValue(internalRef.current, value)
              }}
            />
          </div>
          <div>
            <UsersSelect
              value={users}
              onChange={users => {
                const value = stringify({ users, groups })
                updateInputValue(internalRef.current, value)
              }}
            />
          </div>
        </div>
      ) : null}
    </>
  )
})

type Paginated<T> = {
  rows: T[]
  total: number
}

type Group = {
  id: string
  displayName: string
  memberCount: number
}

type User = {
  id: string
  email: string
  givenName: string
  familyName: string
}

async function getGroups(options?: {
  searchParams?: URLSearchParams | ConstructorParameters<typeof URLSearchParams>[0]
}): Promise<Paginated<Group>> {
  const search = new URLSearchParams(options?.searchParams).toString() ?? ''
  const data = await fetch(`/organizations/:organizationId/groups?${search}`)

  return { rows: data, total: data.length }
}

async function getUsers(options?: {
  searchParams?: URLSearchParams | ConstructorParameters<typeof URLSearchParams>[0]
}): Promise<Paginated<User>> {
  const search = new URLSearchParams(options?.searchParams).toString() ?? ''
  const data = await fetch(`/organizations/:organizationId/users?${search}`)

  return { rows: data.rows, total: data.total }
}

const groupsQuery = {
  queryKey: ['/organizations/:organizationId/groups'],
  queryFn: async () => {
    const response = await getGroups()
    return response.rows
  },
  staleTime: Number.POSITIVE_INFINITY
}

const usersQuery = {
  queryKey: ['/organizations/:organizationId/users'],
  queryFn: async () => {
    const pageSize = 25_000

    const { total } = await getUsers({
      searchParams: {
        limit: String(0),
        offset: String(0)
      }
    })
    const pages = Math.ceil(total / pageSize)

    const requests = []
    for (let i = 0; i < pages; i++) {
      requests.push(
        getUsers({
          searchParams: {
            limit: String(pageSize),
            offset: String(pageSize * i)
          }
        })
      )
    }

    const responses = await Promise.all(requests)
    const users = responses.flatMap(response => response.rows)

    return users.map(user => ({ ...user, displayName: `${user.givenName} ${user.familyName}` }))
  },
  staleTime: Number.POSITIVE_INFINITY
}

type GroupsSelectProps = Pick<AsyncMultiSelectProps, 'value' | 'onChange'>

function GroupsSelect(props: GroupsSelectProps) {
  const queryClient = useQueryClient()

  async function fetchOptions() {
    const [users, groups] = await Promise.all([queryClient.fetchQuery(usersQuery), queryClient.fetchQuery(groupsQuery)])

    return [{ id: 'everyone', displayName: 'Everyone', memberCount: users.length }, ...groups]
  }

  const numberFormatter = new Intl.NumberFormat()
  const formatMembersCount = (memberCount: number) => {
    const label = memberCount === 1 ? 'member' : 'members'
    return `${numberFormatter.format(memberCount)} ${label}`
  }

  return (
    <AsyncMultiSelect
      {...props}
      placeholder="Select groups..."
      fetchOptions={fetchOptions}
      getOptionLabel={option => option.displayName}
      getOptionDetail={option => {
        if (option.memberCount == null) return
        return formatMembersCount(option.memberCount)
      }}
    />
  )
}

type UserSelectProps = Pick<AsyncMultiSelectProps, 'value' | 'onChange'>

function UsersSelect(props: UserSelectProps) {
  const queryClient = useQueryClient()

  async function fetchOptions() {
    return queryClient.fetchQuery(usersQuery)
  }

  return (
    <AsyncMultiSelect
      {...props}
      placeholder="Select users..."
      fetchOptions={fetchOptions}
      getOptionLabel={option => option.displayName}
      getOptionDetail={option => option.email}
    />
  )
}

const MultiValueContainer: typeof components.MultiValueContainer = props => {
  return (
    <components.MultiValueContainer
      {...props}
      innerProps={{
        // eslint-disable-next-line react/prop-types
        ...props.innerProps,
        // eslint-disable-next-line react/prop-types
        className: cn('w-full justify-between', props.innerProps.className)
      }}
    />
  )
}

type AsyncMultiSelectProps<Option = unknown> = {
  fetchOptions: () => Promise<Option[]>
  filterOptions?: (optionss: Option[], inputValue: string) => Option[]

  value: string[]
  onChange: (value: string[]) => void

  placeholder?: string

  getOptionValue?: (option: Option) => string | undefined
  getOptionLabel?: (option: Option) => string | undefined
  getOptionDetail?: (option: Option) => string | undefined
}

function AsyncMultiSelect<Option extends { id: string; [key: string]: unknown }>(props: AsyncMultiSelectProps<Option>) {
  // Generate a unique queryKey for each instance to isolate the internal `useQuery`
  // (we rely on the `fetchOptions` method implementing any appropriate caching)
  const instanceQueryKey = useRef([Math.random()])

  const { data: byId, isLoading } = useQuery({
    queryKey: instanceQueryKey.current,
    queryFn: props.fetchOptions,
    select: data => _.keyBy(data, 'id')
  })

  const value = props.value.map(id => ({ id, ...byId?.[id] }))

  const getOptionValue = (option: Option) => props.getOptionValue?.(option) || option.id
  const getOptionLabel = (option: Option) => props.getOptionLabel?.(option) || (isLoading ? 'Loading...' : 'Unknown')
  const getOptionDetail = (option: Option) => props.getOptionDetail?.(option) || option.id

  const filterOptions =
    props.filterOptions ??
    ((options, inputValue) => {
      // Using lodash sequence to leverage shortcut fusion
      // (will only test the minimum number of options)
      return _(options)
        .filter(option => {
          if (!inputValue) return true

          const label = getOptionLabel(option).toLowerCase()
          return label.includes(inputValue.toLowerCase().trim())
        })
        .take(10)
        .value()
    })

  async function loadOptions(inputValue: string) {
    const options = await props.fetchOptions()
    return filterOptions(options, inputValue)
  }

  return (
    <AsyncSelect
      placeholder={props.placeholder}
      isMulti
      isClearable={false}
      isLoading={isLoading}
      value={value as Option[]}
      loadOptions={loadOptions}
      defaultOptions
      getOptionValue={getOptionValue}
      getOptionLabel={getOptionLabel}
      formatOptionLabel={option => (
        <>
          <div className="truncate text-sm">{getOptionLabel(option)}</div>
          <div className="truncate text-xs text-gray-500">{getOptionDetail(option)}</div>
        </>
      )}
      onChange={value => props.onChange(value.map(option => option.id))}
      className="[&_input]:!ring-0"
      components={{ MultiValueContainer }}
    />
  )
}

Audience.displayName = 'Audience'

export default Audience
